import { TrendingDown, TrendingFlat, TrendingUp } from '@mui/icons-material';
import { Box, Paper, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

interface StatCardProps {
  icon: ReactNode;
  label: string;
  value: number;
  color: string;
  navigateTo?: string;
  /** In-page action alternative to navigateTo (e.g. toggle a filter). */
  onClick?: () => void;
  trend?: { current: number; previous: number };
  /** Half-height variant (compact strips, e.g. the Tests page KPI row). */
  dense?: boolean;
}

export const StatCard = ({ icon, label, value, color, navigateTo, onClick, trend, dense }: StatCardProps) => {
  const navigate = useNavigate();
  const clickable = Boolean(navigateTo || onClick);

  const trendDiff = trend ? trend.current - trend.previous : null;
  const trendIcon = trendDiff != null
    ? trendDiff > 0
      ? <TrendingUp sx={{ fontSize: 14 }} />
      : trendDiff < 0
        ? <TrendingDown sx={{ fontSize: 14 }} />
        : <TrendingFlat sx={{ fontSize: 14 }} />
    : null;
  const trendColor = trendDiff != null
    ? trendDiff > 0 ? '#4CAF50' : trendDiff < 0 ? '#F44336' : '#9E9E9E'
    : undefined;

  return (
    <Paper
      elevation={0}
      onClick={onClick ?? (navigateTo ? () => navigate(navigateTo) : undefined)}
      sx={{
        p: dense ? 0.75 : 1.5,
        px: dense ? 1.25 : 1.5,
        borderRadius: dense ? '8px' : '10px',
        border: '1px solid',
        borderColor: 'border.color',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        '&:hover': clickable
          ? { transform: 'translateY(-2px) scale(1.01)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }
          : {},
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: dense ? 1 : 1.5 }}>
        <Box
          sx={{
            width: dense ? 26 : 38,
            height: dense ? 26 : 38,
            borderRadius: dense ? '6px' : '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${color}18`,
            color,
            '& .MuiSvgIcon-root': { fontSize: dense ? 15 : 20 },
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Typography sx={{ fontWeight: 700, fontSize: dense ? '1.05rem' : '1.5rem', lineHeight: 1 }}>
              {value}
            </Typography>
            {dense && (
              <Typography noWrap color="text.secondary" sx={{ fontSize: '0.68rem', fontWeight: 600 }}>
                {label}
              </Typography>
            )}
            {trendDiff != null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: trendColor }}>
                {trendIcon}
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600 }}>
                  {trendDiff > 0 ? '+' : ''}{trendDiff} vs last mo.
                </Typography>
              </Box>
            )}
          </Box>
          {!dense && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
              {label}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
